/**
 * Lo que alguien teclea como número, pasado a como viaja por la API.
 *
 * **En Perú el decimal se escribe con coma**, y el teclado numérico de una
 * tablet con la región puesta solo ofrece coma. Leerlo con `parseFloat`
 * guardaba un costo de «38,50» como 38 sin avisar, y una cantidad «9,06»
 * volvía como un error que no decía qué campo estaba mal. Aquí la coma y el
 * punto valen lo mismo, y lo que no es un número se dice con el nombre del
 * campo.
 */

/**
 * El IGV de las compras, en porcentaje.
 *
 * Es el general de la ley y el que traen las facturas de los proveedores. Se
 * usa solo para quitárselo al precio que se teclea: al almacén el costo entra
 * **sin IGV**, porque para una empresa que lo declara es crédito fiscal y no
 * costo de la mercadería.
 */
export const IGV_COMPRAS = 18;

/** Lo que no se puede mandar, con el nombre del campo delante. */
export class EntradaInvalida extends Error {}

/**
 * `"9,06"` → `"9.06"`. Hasta tres decimales, sin signo.
 *
 * Tres y no dos: la receta va en gramos y el stock en kilos, y 200 g son
 * `0.200`.
 */
export function normalizarCantidad(texto: string, campo = 'La cantidad'): string {
  const limpio = texto.trim().replace(/\s/g, '').replace(',', '.');

  if (!/^\d{1,9}(\.\d{1,3})?$/.test(limpio)) {
    throw new EntradaInvalida(
      `${campo} va con números, sin signo y con hasta tres decimales: por ejemplo 2,5.`,
    );
  }

  return limpio;
}

/** Una cantidad que puede no ponerse: vacía es «no dicha», no cero. */
export function cantidadOpcional(texto: string | undefined, campo: string): string | undefined {
  return texto === undefined || texto.trim() === '' ? undefined : normalizarCantidad(texto, campo);
}

/**
 * `"38,50"` → `38.5`. Hasta cuatro decimales, sin signo.
 *
 * Devuelve `NaN` si no es un importe, para que la pantalla lo pueda comprobar
 * antes de habilitar el botón.
 */
export function leerImporte(texto: string): number {
  const limpio = texto.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d{1,9}(\.\d{1,4})?$/.test(limpio)) return Number.NaN;
  return Number.parseFloat(limpio);
}

/*
 * La recepción leía la cantidad con `leerImporte`, que deja cuatro decimales,
 * y después la cortaba con `toFixed(3)`: «2,5555 kg» entraba como 2,556 sin
 * que nadie lo viera. Ahora usa `leerCantidad` (tres, lo que guarda el
 * almacén) y la pantalla dice por qué no lo acepta.
 */
/** Por qué no se acepta lo tecleado como cantidad, o nulo si está bien o vacío. */
export function problemaDeCantidad(texto: string, unidad?: string | null): string | null {
  const limpio = texto.trim().replace(/\s/g, '').replace(',', '.');
  if (limpio === '') return null;
  if (Number.isNaN(leerCantidad(texto))) {
    if (/^\d{1,9}\.\d{4,}$/.test(limpio)) return 'La cantidad lleva hasta tres decimales.';
    return 'La cantidad va con números: por ejemplo 9,06.';
  }
  /*
   * Lo que se cuenta entero —unidades, cajas, botellas— no lleva fracción.
   * «1.500» en una ferretería son mil quinientos tornillos, y la carga inicial
   * ya lo preguntaba; la recepción lo leía como 1,5 sin decir nada y entraba
   * uno y medio (8 oct, segunda ronda del almacén). La misma regla en los dos
   * sitios: en kilos o metros 1,5 es 1,5; en unidades se pregunta.
   */
  if (unidad !== undefined && unidad !== null && !seFracciona(unidad) && limpio.includes('.')) {
    const entero = limpio.replace('.', '');
    return /^[1-9]\d{0,2}\.\d{3}$/.test(limpio)
      ? `¿${entero} o ${String(Number(limpio)).replace('.', ',')}? En ${unidad} va entero: escribe ${entero} si son ${entero}.`
      : `En ${unidad} va entero, sin decimales.`;
  }
  return null;
}

/**
 * Si una unidad de stock se vende o se recibe en fracciones: el kilo, el
 * litro, el metro. Las demás —unid, caja, botella, paquete— se cuentan
 * enteras. Por el código, que es lo que el servidor manda (`unit`).
 */
export function seFracciona(unidad: string): boolean {
  return /^(kg|g|gr|mg|l|lt|ml|cl|m|mt|cm|mm|lb|oz|gal)\.?$/i.test(unidad.trim());
}

/** `leerCantidad` con la regla de la unidad: lo que pregunta no se lee. */
export function leerCantidadEn(texto: string, unidad?: string | null): number {
  return problemaDeCantidad(texto, unidad) === null ? leerCantidad(texto) : Number.NaN;
}

/** Lo mismo que `leerImporte`, pero se niega con el nombre del campo. */
export function importe(texto: string, campo = 'El costo'): number {
  const valor = leerImporte(texto);
  if (Number.isNaN(valor)) {
    throw new EntradaInvalida(`${campo} va con números y hasta cuatro decimales: por ejemplo 38,50.`);
  }
  return valor;
}

/**
 * El costo por unidad de stock, como viaja al servidor: `"12.7119"`.
 *
 * Lo tecleado es lo que dice la factura —con IGV o sin él, por caja o por
 * unidad—, y al almacén entra sin IGV y por unidad de stock. Antes esa cuenta
 * se hacía con `number` y se mandaba como número: `38.5 / 1.18 / 12` daba
 * 2.7189265536723164, y lo que llegaba dependía de cómo redondeara cada paso.
 * Aquí va en enteros —diezmilésimas del precio y milésimas del factor— con un
 * solo redondeo al final, medio hacia arriba, a los cuatro decimales de la
 * columna. Y sale como texto: el dinero no viaja como `number`.
 *
 * `null` si lo tecleado no es un importe, o el factor no es positivo.
 */
export function costoPorUnidadDeStock(
  precioTecleado: string,
  opciones: { conIgv?: boolean; factor?: string | null } = {},
): string | null {
  const precio = enteroConDecimales(precioTecleado, 4);
  if (precio === null) return null;

  const factor = opciones.factor == null ? 1000n : enteroConDecimales(opciones.factor, 3);
  if (factor === null || factor <= 0n) return null;

  // precio / (1 + IGV) / factor, en diezmilésimas: el factor llega en
  // milésimas, así que se multiplica por mil arriba.
  const numerador = precio * 1000n * (opciones.conIgv === true ? 100n : 1n);
  const denominador = factor * (opciones.conIgv === true ? BigInt(100 + IGV_COMPRAS) : 1n);
  const resultado = (numerador * 2n + denominador) / (denominador * 2n);

  const enteros = resultado / 10_000n;
  const decimales = String(resultado % 10_000n).padStart(4, '0');
  return `${enteros}.${decimales}`;
}

/** `"38,5"` con 4 decimales → `385000n`. `null` si no es un número sin signo. */
function enteroConDecimales(texto: string, decimales: number): bigint | null {
  const limpio = texto.trim().replace(/\s/g, '').replace(',', '.');
  const partes = new RegExp(`^(\\d{1,9})(?:\\.(\\d{1,${decimales}}))?$`).exec(limpio);
  if (partes === null) return null;

  return BigInt(partes[1]!) * 10n ** BigInt(decimales) + BigInt((partes[2] ?? '').padEnd(decimales, '0'));
}

/** Un número de tandas o de días: positivo y, si hace falta, con decimales. */
export function leerPositivo(texto: string): number {
  const valor = leerImporte(texto);
  return Number.isNaN(valor) || valor <= 0 ? Number.NaN : valor;
}

/**
 * `"9,5"` → `9.5`, para enseñar una diferencia mientras se teclea.
 *
 * `parseFloat("9,5")` da 9: la diferencia de un conteo o de un ajuste salía
 * medio kilo equivocada justo en la pantalla que existe para verla. Devuelve
 * `NaN` si no es una cantidad.
 */
export function leerCantidad(texto: string): number {
  const limpio = texto.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d{1,9}(\.\d{1,3})?$/.test(limpio)) return Number.NaN;
  return Number.parseFloat(limpio);
}

/**
 * El teléfono de la ficha, como lo pide `wa.me`: con el 51 delante.
 *
 * Un celular peruano se guarda como «987 654 321», y `wa.me/987654321` abre
 * un número que no existe —o el de otro país—. Nueve dígitos que empiezan en 9
 * son un celular de aquí y llevan el 51; lo que ya trae código se deja. Un
 * fijo no tiene WhatsApp, y vacío abre la lista de contactos.
 */
export function numeroParaWhatsapp(telefono: string | null): string {
  const digitos = (telefono ?? '').replace(/\D/g, '');
  return /^9\d{8}$/.test(digitos) ? `51${digitos}` : digitos;
}
